import { InlineBanner } from "components/ui/inline-banner";
import { InteractiveTextLink } from "components/ui/interactive-text-link";
import React from "react";
import { useCrmMode } from "store/selectors/hooks";
import { WorkflowActionSummary } from "../workflow-action-summary";
import { useWorkflowDelayDescription } from "../workflow-delay-summary";
import {
    WorkflowClientStatusesSummary,
    WorkflowTriggerSummary,
} from "../workflow-trigger-summary";
import { workflowEntityTypeLabel } from "./builder-helper";
import { SequenceStrip } from "./sequence-strip";
import { WizardStepProps } from "./wizard-types";

interface RowProps {
    label: string;
    children: React.ReactNode;
}

const Row: React.FC<RowProps> = props => {
    const { label, children } = props;
    return (
        <div className="flex gap-3 text-sm">
            <span className="w-28 shrink-0 text-gray-400">{label}</span>
            <div className="text-gray-700 min-w-0 flex-1">{children}</div>
        </div>
    );
};

export const WizardReview: React.FC<WizardStepProps> = props => {
    const { workflow, workflowSettings, isDraft, editable, publishBlocker, onShowSteps } =
        props;

    const delayDescription = useWorkflowDelayDescription(workflow, workflowSettings);
    const { mode } = useCrmMode();

    return (
        <div className="space-y-5">
            <div>
                <h3 className="font-medium text-gray-900">
                    {isDraft ? "Review & publish" : "Review"}
                </h3>
                <p className="text-sm text-gray-500">
                    {isDraft
                        ? "Here's what will happen once you publish this workflow."
                        : "Here's what this workflow does."}
                </p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
                <Row label="Workflow">
                    <span className="font-medium">{workflow.name}</span>
                </Row>
                <Row label="Record type">
                    {workflowEntityTypeLabel(workflow.attachTo, mode)}
                </Row>
                <Row label="Trigger">
                    <div className="space-y-2">
                        <WorkflowTriggerSummary workflow={workflow} />
                        <WorkflowClientStatusesSummary workflow={workflow} />
                    </div>
                </Row>
                <Row label="Step">
                    <SequenceStrip workflow={workflow} />
                </Row>
                <Row label="Action">
                    <WorkflowActionSummary
                        workflow={workflow}
                        workflowSettings={workflowSettings}
                    />
                </Row>
                {delayDescription ? <Row label="Timing">{delayDescription}</Row> : null}
            </div>
            {isDraft ? (
                editable && publishBlocker ? (
                    <InlineBanner type="warning">
                        This workflow is not ready to publish yet. {publishBlocker}{" "}
                        <InteractiveTextLink onClick={onShowSteps}>
                            Go to the Steps page
                        </InteractiveTextLink>
                    </InlineBanner>
                ) : (
                    <InlineBanner type="info">
                        This workflow is a draft and will not run until you publish it.
                        Publish it below when everything looks right, or save it as a
                        draft and finish later.
                    </InlineBanner>
                )
            ) : (
                <InlineBanner type="success">
                    This workflow is published and runs whenever its trigger is met.
                </InlineBanner>
            )}
        </div>
    );
};
